import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Cloud, RefreshCw, UploadCloud, CheckCircle, AlertTriangle, HardDrive, FolderOpen } from "lucide-react";
import { API } from "../../../App";
import { toast } from "sonner";
import { format } from "date-fns";

const formatBytes = (bytes) => {
  if (bytes === 0 || bytes == null) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(k)), sizes.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const CATEGORY_LABELS = {
  business_logos: "Business Logos",
  inventory: "Inventory Photos",
  sounds: "Notification Sounds",
  logos: "Tech Panel Logos",
  sms_media: "SMS/MMS Media",
  job_attachments: "Job Attachments",
};

const KV = ({ label, value, mono = false, testId }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-border/40 last:border-b-0" data-testid={testId}>
    <span className="text-xs text-muted-foreground whitespace-nowrap">{label}</span>
    <span className={`text-xs text-foreground text-right break-all ${mono ? "font-mono" : ""}`}>
      {value == null || value === "" ? <span className="text-muted-foreground/50">—</span> : value}
    </span>
  </div>
);

export const StoragePanel = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  const authConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const fetchInfo = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/storage-info`, authConfig());
      setInfo(res.data);
    } catch (e) {
      if (!silent) toast.error("Failed to load storage info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runMigration = async () => {
    setMigrating(true);
    setMigrateResult(null);
    try {
      const res = await axios.post(`${API}/admin/storage-migrate`, {}, authConfig());
      setMigrateResult(res.data);
      if (res.data.failed > 0) {
        toast.error(`Migration finished with ${res.data.failed} failures`);
      } else {
        toast.success(`Migrated ${res.data.migrated} files (${res.data.skipped} already stored)`);
      }
      fetchInfo(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-theme">
        <CardContent className="p-12 text-center text-muted-foreground text-sm">Loading storage info…</CardContent>
      </Card>
    );
  }
  if (!info) {
    return (
      <Card className="glass-card border-theme">
        <CardContent className="p-12 text-center text-muted-foreground text-sm">
          Failed to load storage info.
          <Button variant="outline" size="sm" className="ml-3 border-theme" onClick={() => fetchInfo()}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const b = info.backend || {};
  const t = info.totals || {};
  const local = info.local_cache || {};

  return (
    <div className="space-y-4" data-testid="storage-panel">
      {/* Backend + totals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="glass-card border-theme" data-testid="storage-backend-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Cloud className="w-4 h-4 text-sky-500" /> Object Storage Backend
              </CardTitle>
              {b.connected ? (
                <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30" variant="outline" data-testid="storage-connected-badge">
                  <CheckCircle className="w-3 h-3 mr-1" /> Connected
                </Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-500 border-red-500/30" variant="outline" data-testid="storage-disconnected-badge">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Not Connected
                </Badge>
              )}
            </div>
            <CardDescription className="text-xs">
              User uploads persist here and survive every redeploy. Local disk is only a fast cache.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <KV label="Provider" value={b.provider} testId="storage-provider" />
            <KV label="API Endpoint" value={b.endpoint} mono />
            <KV label="App Path Prefix" value={`${b.app_prefix}/uploads/`} mono />
            <KV label="Access Key" value={b.key_configured ? "Configured (EMERGENT_LLM_KEY)" : "MISSING"} />
            {b.error && <KV label="Connection Error" value={b.error} mono />}
          </CardContent>
        </Card>

        <Card className="glass-card border-theme" data-testid="storage-totals-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-foreground text-base flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-violet-500" /> Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-muted/40 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-foreground" data-testid="storage-total-files">{t.active_files ?? 0}</p>
                <p className="text-[11px] text-muted-foreground">Stored Files</p>
              </div>
              <div className="bg-muted/40 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-foreground" data-testid="storage-total-bytes">{formatBytes(t.active_bytes)}</p>
                <p className="text-[11px] text-muted-foreground">Total Size</p>
              </div>
            </div>
            <KV label="Soft-deleted files" value={t.deleted_files ?? 0} />
            <KV label="Local-only files (store was unreachable)" value={t.local_only_files ?? 0} />
            <KV label="Local cache" value={`${local.files ?? 0} files · ${formatBytes(local.bytes)}`} />
            <KV label="Cache path" value={local.path} mono />
          </CardContent>
        </Card>
      </div>

      {/* Categories */}
      <Card className="glass-card border-theme" data-testid="storage-categories-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-foreground text-base flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-amber-500" /> By Category
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => fetchInfo()} className="border-theme text-foreground" data-testid="storage-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {(info.categories || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No files registered yet — new uploads will appear here.</p>
          ) : (
            <div className="rounded-lg border border-theme overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-theme-surface">
                  <tr className="text-muted-foreground text-left text-xs">
                    <th className="p-2">Category</th>
                    <th className="p-2 text-right">Files</th>
                    <th className="p-2 text-right">Size</th>
                    <th className="p-2 text-right">Deleted</th>
                  </tr>
                </thead>
                <tbody>
                  {info.categories.map((c) => (
                    <tr key={c.category} className="border-t border-theme/50 text-foreground" data-testid={`storage-cat-${c.category}`}>
                      <td className="p-2 text-xs font-medium">{CATEGORY_LABELS[c.category] || c.category}</td>
                      <td className="p-2 text-right text-xs">{c.active_count}</td>
                      <td className="p-2 text-right text-xs">{formatBytes(c.active_bytes)}</td>
                      <td className="p-2 text-right text-xs text-muted-foreground">{c.deleted_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Migration tool */}
      <Card className="glass-card border-theme" data-testid="storage-migrate-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <UploadCloud className="w-4 h-4 text-emerald-500" /> Migrate Local Files
          </CardTitle>
          <CardDescription className="text-xs">
            Pushes every file still sitting only on local disk into Object Storage. Safe to run repeatedly — already-stored files are skipped.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={runMigration} disabled={migrating || !b.connected} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="storage-migrate-btn">
            <UploadCloud className={`w-4 h-4 mr-2 ${migrating ? "animate-pulse" : ""}`} />
            {migrating ? "Migrating…" : "Migrate Local Files to Object Storage"}
          </Button>
          {migrateResult && (
            <div className="flex flex-wrap gap-2" data-testid="storage-migrate-result">
              <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/20" variant="outline">{migrateResult.migrated} migrated ({formatBytes(migrateResult.migrated_bytes)})</Badge>
              <Badge variant="outline" className="border-theme text-muted-foreground">{migrateResult.skipped} already stored</Badge>
              {migrateResult.failed > 0 && (
                <Badge className="bg-red-500/15 text-red-500 border-red-500/20" variant="outline">{migrateResult.failed} failed</Badge>
              )}
            </div>
          )}
          {migrateResult?.errors?.length > 0 && (
            <div className="text-[11px] text-red-400 font-mono space-y-0.5">
              {migrateResult.errors.map((e, i) => <p key={i}>{e}</p>)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent uploads */}
      <Card className="glass-card border-theme" data-testid="storage-recent-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-foreground text-base">Recent Uploads</CardTitle>
        </CardHeader>
        <CardContent>
          {(info.recent_uploads || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No uploads registered yet.</p>
          ) : (
            <div className="rounded-lg border border-theme overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-theme-surface">
                  <tr className="text-muted-foreground text-left text-xs">
                    <th className="p-2">File</th>
                    <th className="p-2">Category</th>
                    <th className="p-2 text-right">Size</th>
                    <th className="p-2">Storage</th>
                    <th className="p-2">When</th>
                  </tr>
                </thead>
                <tbody>
                  {info.recent_uploads.map((f, i) => (
                    <tr key={i} className="border-t border-theme/50 text-foreground">
                      <td className="p-2 text-xs font-mono break-all max-w-[280px]">{f.rel_path}</td>
                      <td className="p-2 text-xs">{CATEGORY_LABELS[f.category] || f.category}</td>
                      <td className="p-2 text-right text-xs">{formatBytes(f.size)}</td>
                      <td className="p-2 text-xs">
                        {f.storage === "emergent"
                          ? <Badge variant="outline" className="bg-emerald-500/15 text-emerald-500 border-emerald-500/20 text-[10px]">Object Store</Badge>
                          : <Badge variant="outline" className="bg-amber-500/15 text-amber-500 border-amber-500/20 text-[10px]">Local Only</Badge>}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">
                        {f.updated_at ? format(new Date(f.updated_at), "MMM d, HH:mm") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StoragePanel;
